import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, MenuItem, Select, FormControl } from "@mui/material";
import globalStyles from "../../global";
import Footer from "../HomeComponent/Footer";
import News from "../globalCountries/countryhomepagescomponent/News";
import FreeDeliveryBanner from "../globalCountries/countryhomepagescomponent/FreeDeliveryBanner";
import { Phone } from "lucide-react";
import { storesAPI } from "../../utils/api";
import { useNavigate } from "react-router-dom";
import Pagination from "@mui/material/Pagination"; // Add this import

// Use the Google Maps API key from your .env file
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Google Map Loader Hook
const useGoogleMaps = (apiKey) => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (window.google && window.google.maps) {
      setIsLoaded(true);
      return;
    }
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=initMap`;
    script.async = true;
    script.defer = true;
    window.initMap = () => setIsLoaded(true);
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
      delete window.initMap;
    };
  }, [apiKey]);

  return isLoaded;
};

// Google Map Component
const GoogleMapCustom = ({
  stores,
  highlightedStoreId,
  setHighlightedStoreId,
}) => {
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const isLoaded = useGoogleMaps(GOOGLE_MAPS_API_KEY);

  useEffect(() => {
    if (!isLoaded || !mapRef.current || !stores.length) return;

    // Clean up old markers
    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];

    const validStores = stores.filter(
      (s) =>
        s.latitude &&
        s.longitude &&
        !isNaN(Number(s.latitude)) &&
        !isNaN(Number(s.longitude))
    );
    let center = { lat: 28.5562, lng: 77.1 }; // Default center (Delhi)
    if (validStores.length > 0) {
      center = {
        lat: Number(validStores[0].latitude),
        lng: Number(validStores[0].longitude),
      };
    }
    const map = new window.google.maps.Map(mapRef.current, {
      center: center,
      zoom: 12,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      clickableIcons: false,
      gestureHandling: "cooperative",
      disableDefaultUI: true,
    });

    validStores.forEach((store) => {
      const isHighlighted = highlightedStoreId === store.id;
      const marker = new window.google.maps.Marker({
        position: {
          lat: Number(store.latitude),
          lng: Number(store.longitude),
        },
        map: map,
        title: store.name,
        icon: {
          url: isHighlighted
            ? `${process.env.REACT_APP_IMAGE_BASE_URL}mPin.png`
            : `${process.env.REACT_APP_IMAGE_BASE_URL}Pin.png`,
          scaledSize: new window.google.maps.Size(46, 46),
        },
        animation: isHighlighted ? window.google.maps.Animation.BOUNCE : null,
      });

      marker.addListener("click", () => {
        setHighlightedStoreId(store.id);
      });

      markersRef.current.push(marker);
    });
    // eslint-disable-next-line
  }, [isLoaded, stores, highlightedStoreId, setHighlightedStoreId]);

  if (!isLoaded)
    return (
      <Box className="w-full h-full flex justify-center items-center">
        Loading map...
      </Box>
    );

  return (
    <Box
      ref={mapRef}
      sx={{
        height: "500px",
        width: "100%",
        background: "#e8f0e2",
        borderRadius: "16px",
        minHeight: 380,
      }}
    />
  );
};

// Main LocateUsPage component
const GlobalLocateUs = () => {
  const { t } = useTranslation();
  const imageBaseUrl = process.env.REACT_APP_IMAGE_BASE_URL;
  const [searchQuery, setSearchQuery] = useState("");
  const [storeLocations, setStoreLocations] = useState([]);
  const [filteredStoreLocations, setFilteredStoreLocations] = useState([]);
  const [highlightedStoreId, setHighlightedStoreId] = useState(null);
  const [page, setPage] = useState(1);
  const [selectedCountry, setSelectedCountry] = useState("");
  const navigate = useNavigate();

  const PAGE_SIZE = 30; // Number of locations per page

  const countryList = [
    { code: "IN", name: "India" },
    { code: "AE", name: "UAE" },
    { code: "SRI", name: "SRI LANKA" },
  ];

  // Calculate paginated data
  const paginatedLocations = filteredStoreLocations.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  // Reset to first page if search/filter changes
  useEffect(() => {
    setPage(1);
  }, [searchQuery, storeLocations]);

  const fetchStores = useCallback(async () => {
    const response = await storesAPI.getStores("IN");
    if (response.success) {
      const formattedStores = response.data.stores.map((store) => ({
        id: store.id,
        name: store.name,
        address: store.address,
        phone: store.mobile,
        storecode: store.storecode,
        latitude: store.latitude,
        longitude: store.longitude,
      }));
      setStoreLocations(formattedStores);
      setFilteredStoreLocations(formattedStores);
      if (formattedStores.length) setHighlightedStoreId(formattedStores[0].id);
    } else {
      setStoreLocations([]);
      setFilteredStoreLocations([]);
    }
  }, []);

  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  useEffect(() => {
    const filtered = storeLocations.filter(
      (location) =>
        location.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        location.address.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredStoreLocations(filtered);
    if (filtered.length) setHighlightedStoreId(filtered[0].id);
    else setHighlightedStoreId(null);
  }, [searchQuery, storeLocations]);

  const handleSearchChange = (e) => setSearchQuery(e.target.value);
  const handleSearchSubmit = (e) => e.preventDefault();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        background: "white",
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: { xs: "280px", md: "440px" },
          bgcolor: "#F2F3F0",
        }}
      >
        <Box
          component="img"
          src={`${imageBaseUrl}locateus2.png`}
          alt={t("locateUs.alt.banner")}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            position: "absolute",
            left: 0,
            top: 0,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 10,
            background:
              "linear-gradient(180deg, rgba(23, 76, 46, 0.85) 0%, rgba(5, 59, 38, 0.85) 100%)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 20,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              color: "#fff",
              textTransform: "uppercase",
              textAlign: "center",
              textShadow: "0 2px 8px rgba(0,0,0,0.26)",
              fontFamily: globalStyles.fonts.primaryUAE,
              fontWeight: 500,
              fontSize: { xs: "40px", md: "55px" },
              lineHeight: "60px",
              letterSpacing: "1%",
            }}
          >
            {t("locateUs.bannerText")}
          </Typography>
        </Box>
      </Box>

      <Box className="container" sx={{ mx: "auto", px: 2, py: 2 }}>
        <Typography
          sx={{
            textAlign: "center",
            textTransform: "uppercase",
            fontWeight: 600,
            mb: 6,
            mt: 7,
            fontFamily: globalStyles.fonts.primaryUAE,

            color: globalStyles.colors.army_green,
            lineHeight: "100%",
            letterSpacing: "1%",
            fontSize: { xs: "24px", sm: "38px", md: "40px" },
          }}
        >
          {t("locateUs.title")}
        </Typography>

        <Box
          component="form"
          onSubmit={handleSearchSubmit}
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" }, // Column on mobile, row on larger screens
            justifyContent: "center",
            alignItems: "center",
            mb: 8,
            gap: { xs: 2, sm: 0 }, // Add gap between items on mobile
            width: { xs: "100%", md: "900px" },
            mx: "auto",
            boxShadow: "0 4px 24px 0 rgba(50,60,60,0.07)",
            borderRadius: "16px",
            background: "#fff",
            overflow: "hidden",
            p: { xs: 2, sm: 0 }, // Padding for mobile
          }}
        >
          {/* Country Dropdown */}
          <FormControl
            variant="outlined"
            sx={{
              minWidth: { xs: "100%", sm: 120, md: 170 },
              background: "#fff",
              border: "none",
              "& .MuiOutlinedInput-root": {
                borderRadius: { xs: "12px", sm: "16px 0 0 16px" },
                height: "60px",
                fontSize: "18px",
                fontWeight: 500,
                color: globalStyles.colors.army_green,
                boxShadow: "none",
                border: "none",
                "& fieldset": { border: "none" },
                "&:hover": { background: "#f6f6f6" },
                "&.Mui-focused": { background: "#f6f6f6" },
              },
              mb: { xs: 1, sm: 0 }, // Margin bottom on mobile
            }}
            size="medium"
          >
            <Select
              displayEmpty
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value)}
              inputProps={{ "aria-label": "Select Your Region" }}
              sx={{
                height: "60px",
                pl: 2,
                fontSize: "18px",
                color: globalStyles.colors.army_green,
                "& .MuiSelect-icon": { color: globalStyles.colors.army_green },
              }}
              renderValue={(selected) =>
                selected
                  ? countryList.find((c) => c.code === selected)?.name
                  : "Select Your Region"
              }
            >
              {countryList.map((country) => (
                <MenuItem key={country.code} value={country.code}>
                  {country.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Divider */}
          <Box
            sx={{
              width: { xs: "100%", sm: "1px" },
              height: { xs: "1px", sm: "38px" },
              background: { xs: "transparent", sm: "#e0e0e0" },
              mx: { xs: 0, sm: 0 },
              my: { xs: 0, sm: "auto" },
              display: { xs: "none", sm: "block" }, // Hide divider on mobile
            }}
          />

          {/* Search Input */}
          <Box
            sx={{
              position: "relative",
              flex: 2,
              minWidth: { xs: "100%", sm: "220px", md: "320px" },
              height: "60px",
              display: "flex",
              alignItems: "center",
              background: "#fff",
              border: "none",
              px: 2,
              transition: "min-width 0.2s",
              borderRadius: { xs: "12px", sm: 0 },
              mb: { xs: 1, sm: 0 }, // Margin bottom on mobile
            }}
          >
            <Box
              component="img"
              src={`${imageBaseUrl}search-left.png`}
              alt="Search Icon"
              sx={{
                position: "absolute",
                top: "50%",
                left: "18px",
                transform: "translateY(-50%)",
                width: "22px",
                height: "22px",
                pointerEvents: "none",
                opacity: 0.6,
              }}
            />
            <Box
              component="img"
              src={`${imageBaseUrl}location-right.png`}
              alt="Location Icon"
              sx={{
                position: "absolute",
                top: "50%",
                right: "12px",
                transform: "translateY(-50%)",
                width: "20px",
                height: "20px",
                pointerEvents: "none",
              }}
            />
            <Box
              component="input"
              type="text"
              placeholder="Search by Area, City, State"
              value={searchQuery}
              onChange={handleSearchChange}
              sx={{
                width: "100%",
                height: "100%",
                pl: "48px",
                pr: "12px",
                fontSize: "18px",
                outline: "none",
                background: "transparent",
                border: "none",
                color: globalStyles.colors.army_green,
                fontWeight: 500,
                fontFamily: globalStyles.fonts.primary,
                "&::placeholder": {
                  color: "#b0b0b0",
                  opacity: 1,
                  fontWeight: 400,
                },
              }}
            />
          </Box>

          {/* Search Button */}
          <Box
            component="button"
            type="submit"
            sx={{
              width: { xs: "100%", sm: "170px" },
              height: "60px",
              color: "#fff",
              background: globalStyles.colors.uclean_green,
              border: "none",
              borderRadius: { xs: "12px", sm: "0 16px 16px 0" },
              fontWeight: 500,
              fontSize: "18px",
              cursor: "pointer",
              textTransform: "capitalize",
              letterSpacing: 0.5,
              transition: "background 0.2s",
              "&:hover": {
                background: "#1b5e20",
              },
              fontFamily: globalStyles.fonts.primaryGlobal,
            }}
          >
            Search
          </Box>
        </Box>
      </Box>

      {/* Main Content (Slider + Map) */}
      <Box
        sx={{
          width: "100%",
          maxWidth: "1200px",
          mx: "auto",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "flex-start",
          justifyContent: "center",
          bgcolor: "#fff",
          py: { xs: 2, md: 4 },
          px: { xs: 1, md: 3 },
          borderRadius: "18px",
          boxShadow: "0 2px 24px 0 rgba(50,60,60,0.07)",
          gap: { xs: 3, md: 4 },
          minHeight: "530px",
        }}
      >
        {/* Location Card Slider/Column */}
        <Box
          sx={{
            width: { xs: "100%", md: "430px" }, // Full width on xs
            minWidth: { md: "340px" },
            maxHeight: { xs: "none", md: "500px" },
            overflowY: { xs: "visible", md: "auto" },
            pr: { md: 2 },
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            mt: 2,
          }}
        >
          {paginatedLocations.length > 0 ? (
            paginatedLocations.map((location) => (
              <Box
                key={location.id}
                sx={{
                  borderRadius: "12px",
                  border:
                    highlightedStoreId === location.id
                      ? "2.5px solid #63C020"
                      : "1.5px solid #E3E3E3",
                  bgcolor: "#fff",
                  p: 2.6,
                  mb: 1,
                  cursor: "pointer",
                  transition: "box-shadow 0.2s, border 0.2s",
                }}
                onMouseEnter={() => setHighlightedStoreId(location.id)}
                onClick={() => setHighlightedStoreId(location.id)}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 1.1 }}>
                  <Box
                    component="img"
                    src={`${imageBaseUrl}mPin.png`}
                    alt="Location Pin"
                    sx={{ width: 26, height: 26, mr: 1.2 }}
                  />
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: globalStyles.colors.army_green,
                      fontSize: {
                        xs: "14px",
                        md: "18px",
                        lg: "18px",
                      },
                    }}
                    variant="regular_text_ur"
                  >
                    {location.storecode}
                  </Typography>
                  <Typography
                    variant="regular_text_ur"
                    sx={{
                      fontWeight: 600,
                      color: globalStyles.colors.army_green,
                      fontSize: {
                        xs: "14px",
                        md: "18px",
                        lg: "18px",
                      },
                    }}
                  >
                    &nbsp;– {location.name}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    mb: 1.8,
                    fontSize: { xs: "12px", sm: "18px", md: "18px" },
                  }}
                  variant="body2"
                >
                  {location.address}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    component="a"
                    href={`tel:${location.phone || ""}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      px: 1.8,
                      py: 0.6,
                      border: "1.5px solid #C7E7D7",
                      borderRadius: "8px",
                      color: globalStyles.colors.army_green,

                      fontFamily: globalStyles.fonts.primary,
                      textDecoration: "none",
                    }}
                  >
                    <Phone
                      size={18}
                      style={{
                        marginRight: 7,
                        color: globalStyles.colors.army_green,
                      }}
                      variant="phone_text"
                    />
                    {location.phone || t("locateUs.storeCard.defaultPhone")}
                  </Box>
                  <Box
                    component="button"
                    onClick={() =>
                      navigate(`/view-more?storecode=${location.storecode}`)
                    }
                    sx={{
                      border: "none",
                      background: "none",
                      color: "#63C020",
                      textDecoration: "underline",
                      ml: 1,
                      p: 0,
                    }}
                    variant="small_text"
                  >
                    View Details
                  </Box>
                </Box>
              </Box>
            ))
          ) : (
            <Typography
              sx={{
                textAlign: "center",
                fontWeight: 600,
                fontSize: "15px",
                color: globalStyles.colors.army_green,
                my: 3,
                fontFamily: globalStyles.fonts.primary,
                letterSpacing: 0.3,
              }}
            >
              NO LOCATIONS FOUND
            </Typography>
          )}

          {/* Pagination Controls */}
          {filteredStoreLocations.length > PAGE_SIZE && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Pagination
                count={Math.ceil(filteredStoreLocations.length / PAGE_SIZE)}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
                shape="rounded"
                siblingCount={0}
                boundaryCount={1}
                size="medium"
                sx={{
                  "& .MuiPagination-ul": {
                    justifyContent: "center",
                    flexWrap: "wrap",
                  },
                  "& .MuiPaginationItem-root": {
                    fontSize: { xs: "14px", sm: "16px" },
                  },
                  "& .Mui-selected": {
                    backgroundColor:
                      globalStyles.colors.army_green + " !important",
                    color: "#fff !important",
                  },
                  "& .MuiPaginationItem-root.Mui-selected:hover": {
                    backgroundColor: globalStyles.colors.army_green,
                  },
                }}
              />
            </Box>
          )}
        </Box>
        {/* Map Area */}
        <Box
          sx={{
            width: { xs: "100%", md: "calc(100% - 370px - 32px)" },
            minWidth: { md: "480px" },
            maxHeight: "500px",
            minHeight: "380px",
            mt: 2,
            flex: 1,
            display: "flex",
            alignItems: "stretch",
            justifyContent: "flex-end",
            // Hide map on very small screens if needed:
            // display: { xs: "none", md: "flex" },
          }}
        >
          <GoogleMapCustom
            stores={paginatedLocations}
            highlightedStoreId={highlightedStoreId}
            setHighlightedStoreId={setHighlightedStoreId}
          />
        </Box>
      </Box>

      <Box sx={{ mt: { xs: 7, md: 12 } }}>
        <FreeDeliveryBanner />
        <News />
        <Box sx={{ height: { sm: "80px", xs: "20px" } }} />
        <Footer />
      </Box>
    </Box>
  );
};

export default GlobalLocateUs;
